import React, { useEffect, useRef } from 'react'
import ThreeDLogoMark from '../common/ThreeDLogoMark'

import {
  scrollFrameAspectRatio,
  scrollFrameCount,
  scrollFrameUrls,
} from '../../data/landing/generated/scrollFrames'

const TARGET_ASPECT_RATIO = scrollFrameAspectRatio || 2.39
const SCROLL_PIXELS_PER_FRAME = 18
const FRAME_EASE = 0.18

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max)
}

function ScrollFrameSection() {
  const sectionRef = useRef(null)
  const canvasRef = useRef(null)
  const offscreenRef = useRef(null)
  const frameImagesRef = useRef([])
  const rafRef = useRef(0)
  const hasLoadedFirstFrameRef = useRef(false)
  const renderedFrameIndexRef = useRef(-1)
  const targetProgressRef = useRef(0)
  const easedProgressRef = useRef(0)
  const motionVelocityRef = useRef(0)
  const scrollDistanceRef = useRef(Math.max(scrollFrameCount * SCROLL_PIXELS_PER_FRAME, 2400))
  const canvasSizeRef = useRef({ w: 0, h: 0 })
  const [isLoaded, setIsLoaded] = React.useState(false)

  useEffect(() => {
    const section = sectionRef.current
    const canvas = canvasRef.current

    if (!section || !canvas || !scrollFrameCount) {
      return undefined
    }

    const context = canvas.getContext('2d', { alpha: false })

    if (!context) {
      return undefined
    }

    // Create offscreen canvas for double-buffering (eliminates flicker)
    const offscreen = document.createElement('canvas')
    const offCtx = offscreen.getContext('2d', { alpha: false })
    offscreenRef.current = { canvas: offscreen, context: offCtx }

    let cancelled = false
    let isAnimating = false

    const syncCanvasSize = () => {
      const bounds = canvas.getBoundingClientRect()
      const pixelRatio = Math.min(window.devicePixelRatio || 1, 2)
      const nextWidth = Math.max(1, Math.round(bounds.width * pixelRatio))
      const nextHeight = Math.max(1, Math.round(bounds.height * pixelRatio))

      if (canvasSizeRef.current.w === nextWidth && canvasSizeRef.current.h === nextHeight) {
        return false
      }

      canvas.width = nextWidth
      canvas.height = nextHeight
      offscreen.width = nextWidth
      offscreen.height = nextHeight
      canvasSizeRef.current = { w: nextWidth, h: nextHeight }

      // Paint with the branded dark base while frames are loading.
      context.fillStyle = '#0b1118'
      context.fillRect(0, 0, nextWidth, nextHeight)
      offCtx.fillStyle = '#0b1118'
      offCtx.fillRect(0, 0, nextWidth, nextHeight)

      return true
    }

    const loadFrame = (frameUrl, index) =>
      new Promise((resolve, reject) => {
        const image = new Image()

        image.decoding = 'async'
        image.fetchPriority = index < 8 ? 'high' : 'auto'
        image.src = frameUrl
        image.onload = () => resolve(image)
        image.onerror = () => reject(new Error(`Failed to load scroll frame: ${frameUrl}`))
      })

    const drawFrameToCanvas = (frameIndex) => {
      const image = frameImagesRef.current[frameIndex]
      if (!image) return

      const { w: canvasWidth, h: canvasHeight } = canvasSizeRef.current
      if (!canvasWidth || !canvasHeight) return

      // Draw to offscreen canvas first (double-buffer)
      offCtx.fillStyle = '#0b1118'
      offCtx.fillRect(0, 0, canvasWidth, canvasHeight)

      const canvasAspectRatio = canvasWidth / canvasHeight
      let drawWidth = canvasWidth
      let drawHeight = Math.round(drawWidth / TARGET_ASPECT_RATIO)

      if (canvasAspectRatio > TARGET_ASPECT_RATIO) {
        drawHeight = canvasHeight
        drawWidth = Math.round(drawHeight * TARGET_ASPECT_RATIO)
      }

      const destinationX = Math.round((canvasWidth - drawWidth) / 2)
      const destinationY = Math.round((canvasHeight - drawHeight) / 2)
      const sourceAspectRatio = image.naturalWidth / image.naturalHeight
      let sourceX = 0
      let sourceY = 0
      let sourceWidth = image.naturalWidth
      let sourceHeight = image.naturalHeight

      if (sourceAspectRatio > TARGET_ASPECT_RATIO) {
        sourceWidth = image.naturalHeight * TARGET_ASPECT_RATIO
        sourceX = (image.naturalWidth - sourceWidth) / 2
      } else if (sourceAspectRatio < TARGET_ASPECT_RATIO) {
        sourceHeight = image.naturalWidth / TARGET_ASPECT_RATIO
        sourceY = (image.naturalHeight - sourceHeight) / 2
      }

      offCtx.imageSmoothingEnabled = true
      offCtx.imageSmoothingQuality = 'high'
      offCtx.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        destinationX,
        destinationY,
        drawWidth,
        drawHeight
      )

      // Swap: copy the fully-rendered offscreen buffer to the visible canvas in one operation
      context.drawImage(offscreen, 0, 0)

      renderedFrameIndexRef.current = frameIndex
    }

    const animationLoop = () => {
      if (cancelled || !hasLoadedFirstFrameRef.current) {
        isAnimating = false
        return
      }

      const delta = targetProgressRef.current - easedProgressRef.current
      const stillMoving = Math.abs(delta) > 0.0003

      if (stillMoving) {
        easedProgressRef.current += delta * FRAME_EASE
      } else {
        easedProgressRef.current = targetProgressRef.current
      }

      motionVelocityRef.current = motionVelocityRef.current * 0.78 + delta * 18

      const nextFrameIndex = clamp(
        Math.round(easedProgressRef.current * (scrollFrameCount - 1)),
        0,
        scrollFrameCount - 1
      )

      syncLogoMotion()

      // Only redraw if the frame actually changed
      if (nextFrameIndex !== renderedFrameIndexRef.current) {
        drawFrameToCanvas(nextFrameIndex)
      }

      if (stillMoving) {
        rafRef.current = window.requestAnimationFrame(animationLoop)
      } else {
        isAnimating = false
        rafRef.current = 0
      }
    }

    const startAnimation = () => {
      if (isAnimating) return
      isAnimating = true
      rafRef.current = window.requestAnimationFrame(animationLoop)
    }

    const syncLogoMotion = () => {
      const progress = clamp(easedProgressRef.current, 0, 1)
      const velocity = clamp(motionVelocityRef.current, -1, 1)
      const momentum = Math.abs(velocity)
      const rotation = progress * 360
      const scale = 0.924 + progress * 0.082 + momentum * 0.024
      const tiltX = 15 + Math.sin(progress * Math.PI * 2) * 2.8 + momentum * 8.5
      const tiltY = -6 + Math.cos(progress * Math.PI * 2) * 3.8 - velocity * 11
      const roll = Math.sin(progress * Math.PI * 2.4) * 3.2 - velocity * 9
      const lift = -10 - Math.sin(progress * Math.PI) * 8.5 - momentum * 10
      const driftX = Math.sin(progress * Math.PI * 4) * 4.5 + velocity * 18
      const counterRotation = -rotation * 0.93 + roll
      const shadowScale = 0.96 + Math.sin(progress * Math.PI) * 0.05 + momentum * 0.14
      const shadowOpacity = 0.32 + Math.sin(progress * Math.PI) * 0.12 + momentum * 0.18
      const highlightX = 32 + velocity * 14
      const shellGlow = 0.08 + momentum * 0.12
      const sheenOpacity = 0.52 + momentum * 0.22

      section.style.setProperty('--scroll-cinema-progress', progress.toFixed(4))
      section.style.setProperty('--scroll-cinema-target-progress', targetProgressRef.current.toFixed(4))
      section.style.setProperty('--scroll-cinema-rotation', `${rotation.toFixed(2)}deg`)
      section.style.setProperty('--scroll-cinema-logo-scale', scale.toFixed(4))
      section.style.setProperty('--scroll-cinema-logo-tilt-x', `${tiltX.toFixed(2)}deg`)
      section.style.setProperty('--scroll-cinema-logo-tilt-y', `${tiltY.toFixed(2)}deg`)
      section.style.setProperty('--scroll-cinema-logo-lift', `${lift.toFixed(2)}px`)
      section.style.setProperty('--scroll-cinema-logo-drift-x', `${driftX.toFixed(2)}px`)
      section.style.setProperty('--scroll-cinema-logo-counter-rotation', `${counterRotation.toFixed(2)}deg`)
      section.style.setProperty('--scroll-cinema-logo-shadow-scale', shadowScale.toFixed(4))
      section.style.setProperty('--scroll-cinema-logo-shadow-opacity', shadowOpacity.toFixed(4))
      section.style.setProperty('--scroll-cinema-logo-highlight-x', `${highlightX.toFixed(2)}%`)
      section.style.setProperty('--scroll-cinema-logo-shell-glow', shellGlow.toFixed(4))
      section.style.setProperty('--scroll-cinema-logo-sheen-opacity', sheenOpacity.toFixed(4))
    }

    const updateFrameTarget = () => {
      const sectionRect = section.getBoundingClientRect()
      const progress = clamp(-sectionRect.top / scrollDistanceRef.current, 0, 1)

      targetProgressRef.current = progress
      syncLogoMotion()
      startAnimation()
    }

    const preloadFrames = async () => {
      const firstFramePromise = loadFrame(scrollFrameUrls[0], 0)
        .then((firstFrame) => {
          if (cancelled) {
            return null
          }

          frameImagesRef.current[0] = firstFrame
          hasLoadedFirstFrameRef.current = true
          renderedFrameIndexRef.current = -1
          setIsLoaded(true)

          // Draw the first frame immediately.
          syncCanvasSize()
          drawFrameToCanvas(0)
          updateFrameTarget()

          return firstFrame
        })
        .catch((error) => {
          console.error(error)
          return null
        })

      scrollFrameUrls.slice(1).forEach((frameUrl, index) => {
        loadFrame(frameUrl, index + 1)
          .then((image) => {
            if (cancelled) {
              return;
            }

            frameImagesRef.current[index + 1] = image;
          })
          .catch((error) => {
            console.error(error)
          })
      })

      await firstFramePromise
    }

    const handleViewportChange = () => {
      scrollDistanceRef.current = Math.max(scrollFrameCount * SCROLL_PIXELS_PER_FRAME, window.innerHeight * 2.75)
      syncCanvasSize()

      // Re-render current frame after resize without flicker
      if (hasLoadedFirstFrameRef.current && renderedFrameIndexRef.current >= 0) {
        drawFrameToCanvas(renderedFrameIndexRef.current)
      }

      updateFrameTarget()
    }

    scrollDistanceRef.current = Math.max(scrollFrameCount * SCROLL_PIXELS_PER_FRAME, window.innerHeight * 2.75)
    syncCanvasSize()
    context.fillStyle = '#0b1118'
    context.fillRect(0, 0, canvas.width, canvas.height)
    syncLogoMotion()

    preloadFrames().catch((error) => {
      console.error(error)
    })

    window.addEventListener('scroll', updateFrameTarget, { passive: true })
    window.addEventListener('resize', handleViewportChange)

    const resizeObserver =
      typeof ResizeObserver === 'undefined'
        ? null
        : new ResizeObserver(() => {
            handleViewportChange()
          })

    if (resizeObserver) {
      resizeObserver.observe(canvas)
    }

    return () => {
      cancelled = true
      isAnimating = false
      hasLoadedFirstFrameRef.current = false
      window.removeEventListener('scroll', updateFrameTarget)
      window.removeEventListener('resize', handleViewportChange)

      if (resizeObserver) {
        resizeObserver.disconnect()
      }

      if (rafRef.current) {
        window.cancelAnimationFrame(rafRef.current)
      }
    }
  }, [])

  if (!scrollFrameCount) {
    return null
  }

  return (
    <section
      ref={sectionRef}
      className="landing-scroll-cinema"
      style={{ '--scroll-cinema-distance': `${Math.max(scrollFrameCount * SCROLL_PIXELS_PER_FRAME, 2400)}px` }}
      aria-hidden="true"
    >
      <div className="landing-scroll-cinema-sticky">
        {!isLoaded && (
          <div className="landing-scroll-cinema-loading">
            <div className="landing-scroll-cinema-shimmer" />
            <span>Loading preview…</span>
          </div>
        )}
        <canvas ref={canvasRef} className="landing-scroll-cinema-canvas" />

        {/* Syniq 3D logo centered inside the donut ring and synced to the scroll rotation */}
        <div className="landing-scroll-cinema-logo" aria-hidden="true">
          <div className="landing-scroll-cinema-logo-orbit">
            <div className="landing-scroll-cinema-logo-stage">
              <ThreeDLogoMark className="landing-scroll-cinema-logo-mark" depth={18} glow="none" />
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ScrollFrameSection
